import React, { useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ShieldCheck,
  Check,
  Loader2,
} from "lucide-react";
import { PACKAGES, SECTORS, LEGAL, formatTL } from "../mock";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const inputCls =
  "w-full bg-[#f5f5f5] rounded-lg px-4 py-3 text-sm outline-none border border-transparent focus:bg-white focus:border-neutral-300 transition-colors placeholder:text-neutral-400";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pkg = PACKAGES.find((p) => p.id === params.get("package")) || PACKAGES[0];
  const [isBusiness, setIsBusiness] = useState(params.get("type") === "bayi");
  const tip = isBusiness ? "İşletme" : "Bireysel";

  const [form, setForm] = useState({
    ad: "",
    soyad: "",
    email: "",
    telefon: "",
    isletme: "",
    sektor: "Diğer",
    sehir: "Balıkesir",
    adres: "",
  });
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [payError, setPayError] = useState("");
  const [legal, setLegal] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.ad.trim().length < 2) errs.ad = "Adınızı giriniz";
    if (form.soyad.trim().length < 2) errs.soyad = "Soyadınızı giriniz";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Geçerli bir e-posta giriniz";
    if (form.telefon.replace(/\D/g, "").length < 10)
      errs.telefon = "Geçerli bir telefon giriniz";
    if (isBusiness && form.isletme.trim().length < 2)
      errs.isletme = "İşletme adını giriniz";
    if (!accepted) errs.accepted = "Sözleşmeyi kabul etmelisiniz";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setLoading(true);
    setPayError("");
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`,
        {
          ad: form.ad,
          soyad: form.soyad,
          email: form.email,
          telefon: form.telefon,
          package_id: pkg.id,
          tip: isBusiness ? "bayi" : "bireysel",
          isletme: form.isletme || null,
          sektor: isBusiness ? form.sektor : null,
          sehir: form.sehir,
          adres: form.adres || null,
          origin: window.location.origin,
        }
      );
      window.location.assign(data.payment_page_url);
    } catch (err) {
      setPayError(err.response?.data?.detail || "Ödeme başlatılamadı, lütfen tekrar deneyin.");
      setLoading(false);
    }
  };

  const goBack = () => navigate("/tanitim");

  /* ---------- FORM ---------- */
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-sm font-semibold text-black hover:text-neutral-500 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Paketlere Dön
        </button>

        <div className="mt-10 grid lg:grid-cols-[1fr_370px] gap-12 items-start">
          {/* SOL: FORM */}
          <form onSubmit={(e) => e.preventDefault()} noValidate>
            <h1 className="text-2xl font-bold text-black">Bilgilerinizi Girin</h1>
            <p className="mt-1.5 text-sm text-neutral-500">
              Ödeme sonrası bu bilgilerle mobil uygulamaya giriş yapacaksınız.
            </p>

            <div className="mt-6 inline-flex bg-[#f0f0f0] rounded-full p-1" data-testid="account-type-toggle">
              {[["Bireysel", false], ["İşletme", true]].map(([label, val]) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`account-type-${val ? "isletme" : "bireysel"}`}
                  onClick={() => setIsBusiness(val)}
                  className={`px-5 py-2 rounded-full text-xs font-semibold transition-colors ${
                    isBusiness === val ? "bg-black text-white" : "text-neutral-600 hover:text-black"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold mb-1.5">
                  Ad <span className="text-red-500">*</span>
                </label>
                <input
                  className={inputCls}
                  placeholder="Adınız"
                  value={form.ad}
                  onChange={set("ad")}
                />
                {errors.ad && (
                  <p className="text-xs text-red-500 mt-1">{errors.ad}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5">
                  Soyad <span className="text-red-500">*</span>
                </label>
                <input
                  className={inputCls}
                  placeholder="Soyadınız"
                  value={form.soyad}
                  onChange={set("soyad")}
                />
                {errors.soyad && (
                  <p className="text-xs text-red-500 mt-1">{errors.soyad}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-semibold mb-1.5">
                E-posta <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className={inputCls}
                placeholder="ornek@email.com"
                value={form.email}
                onChange={set("email")}
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-xs font-semibold mb-1.5">
                Telefon <span className="text-red-500">*</span>
              </label>
              <input
                className={inputCls}
                placeholder="5xx xxx xx xx"
                value={form.telefon}
                onChange={set("telefon")}
              />
              {errors.telefon && (
                <p className="text-xs text-red-500 mt-1">{errors.telefon}</p>
              )}
            </div>

            {isBusiness && (
              <>
                <div className="mt-8 flex items-center gap-2">
                  <span className="text-base">🏢</span>
                  <span className="text-sm font-bold text-black">
                    İşletme Bilgileri
                  </span>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold mb-1.5">
                    İşletme Adı <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={inputCls}
                    placeholder="Örn: Balıkesir Tekstil A.Ş."
                    value={form.isletme}
                    onChange={set("isletme")}
                  />
                  {errors.isletme && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.isletme}
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold mb-2">
                    Sektör
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {SECTORS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, sektor: s }))}
                        className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${
                          form.sektor === s
                            ? "bg-black text-white"
                            : "bg-[#f0f0f0] text-neutral-600 hover:bg-neutral-200"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold mb-1.5">
                    Şehir
                  </label>
                  <input
                    className={inputCls}
                    value={form.sehir}
                    onChange={set("sehir")}
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-semibold mb-1.5">
                    Adres <span className="text-neutral-400 font-normal">(opsiyonel)</span>
                  </label>
                  <textarea
                    rows={3}
                    className={`${inputCls} resize-none`}
                    placeholder="Fatura adresiniz"
                    value={form.adres}
                    onChange={set("adres")}
                  />
                </div>
              </>
            )}

            <div className="mt-6 flex items-start gap-2.5">
              <button
                type="button"
                onClick={() => setAccepted((a) => !a)}
                className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                  accepted ? "bg-black border-black" : "bg-white border-neutral-300"
                }`}
              >
                {accepted && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </button>
              <p className="text-xs text-neutral-600 leading-5">
                <button
                  type="button"
                  onClick={() => setLegal(LEGAL.sozlesme)}
                  className="font-semibold text-black underline underline-offset-2 hover:text-neutral-500"
                >
                  Mesafeli satış sözleşmesini
                </button>{" "}
                ve{" "}
                <button
                  type="button"
                  onClick={() => setLegal(LEGAL.kvkk)}
                  className="font-semibold text-black underline underline-offset-2 hover:text-neutral-500"
                >
                  gizlilik politikasını
                </button>{" "}
                okudum, kabul ediyorum.
              </p>
            </div>
            {errors.accepted && (
              <p className="text-xs text-red-500 mt-1.5">{errors.accepted}</p>
            )}

            <button
              type="button"
              data-testid="iyzico-pay-btn"
              onClick={submit}
              disabled={loading}
              className="mt-6 w-full bg-[#1e2a4a] text-white text-sm font-medium py-4 rounded-lg hover:bg-[#2b3a63] disabled:opacity-60 disabled:cursor-wait transition-colors inline-flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Güvenli ödeme sayfası açılıyor...
                </>
              ) : (
                <img
                  src="/iyzico-ode.png"
                  alt="iyzico ile öde"
                  className="h-7 w-auto object-contain"
                />
              )}
            </button>
            {payError && (
              <p data-testid="payment-error" className="text-xs text-red-500 mt-2 text-center">
                {payError}
              </p>
            )}

            <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-green-600">
              <ShieldCheck className="w-3.5 h-3.5" />
              iyzico SSL şifreleme ile güvenli ödeme
            </div>
          </form>

          {/* SAĞ: SİPARİŞ ÖZETİ */}
          <aside className="bg-[#fafafa] border border-neutral-100 rounded-2xl p-6 lg:sticky lg:top-8">
            <h2 className="text-base font-bold text-black">Sipariş Özeti</h2>
            <div className="border-t border-neutral-200 mt-4 pt-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Paket:</span>
                <span data-testid="summary-package-name" className="font-semibold">{pkg.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Tip:</span>
                <span className="font-semibold">{tip}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Süre:</span>
                <span className="font-semibold">{pkg.label}</span>
              </div>
            </div>

            <div className="border-t border-neutral-200 mt-4 pt-4 space-y-2.5">
              {pkg.features.map((f) => (
                <div key={f} className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />
                  </span>
                  <span className="text-xs text-neutral-600">{f}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-neutral-200 mt-4 pt-4 flex items-end justify-between">
              <span className="text-sm font-bold text-black">Toplam</span>
              <div className="text-right">
                <div className="text-2xl font-extrabold text-black tracking-tight">
                  ₺{formatTL(pkg.price)},00
                </div>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-neutral-200">
              <img
                src="/payment-methods.png"
                alt="iyzico güvencesiyle: Mastercard, Visa, American Express, Troy"
                className="w-full h-auto object-contain"
              />
            </div>

          </aside>
        </div>
      </div>

      <Dialog open={!!legal} onOpenChange={() => setLegal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{legal?.title}</DialogTitle>
            <DialogDescription className="sr-only">
              {legal?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-neutral-600 leading-6 whitespace-pre-line max-h-[60vh] overflow-y-auto pr-2">
            {legal?.body}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegisterPage;
